import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "motion/react";
import { Loader2, AlertCircle, ArrowLeft } from "lucide-react";
import { trpc } from "~/api/trpc";
import { useLanguageStore } from "~/stores/language-store";
import { useCustomerAuthStore } from "~/stores/customer-auth-store";
import PrimaryActionButton from "~/components/new/PrimaryActionButton";
import KaliaLogo from "~/components/new/KaliaLogo";
import { buildDrawerReopenUrl, consumeEmbedReturnPage } from "~/lib/embed";

const LoginCallback = () => {
    const { t } = useLanguageStore();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { login, returnUrl, clearReturnUrl } = useCustomerAuthStore();

    const [error, setError] = useState<string | null>(null);
    const [isVerifying, setIsVerifying] = useState(true);

    const token = searchParams.get("token");

    const loginWithTokenMutation = trpc.publicCustomer.loginWithToken.useMutation();

    useEffect(() => {
        const verifyToken = async () => {
            if (!token) {
                setError(t('loginVerificationFailed') as string);
                setIsVerifying(false);
                return;
            }

            try {
                const result = await loginWithTokenMutation.mutateAsync({ token });

                if (result.valid && result.user) {
                    // When a short-lived handoff token is exchanged, the server returns a fresh
                    // long-lived session token. Persist that instead of the URL token so the
                    // short-lived handoff token is never stored.
                    const sessionToken = result.token ?? token;

                    // Store the token and customer data
                    login(sessionToken, {
                        email: result.user.email || "",
                        firstName: result.user.firstName || "",
                        lastName: result.user.lastName || "",
                        phone: result.user.phone,
                    });

                    const next = searchParams.get("next");
                    const isSafeNext = next && /^\/waitlist-claim-your-slot\/[0-9a-f-]{36}$/.test(next);
                    const funnelPath = returnUrl || "/";
                    const embedReturnPage = consumeEmbedReturnPage();
                    clearReturnUrl();
                    
                    // Small delay for UX
                    await new Promise(resolve => setTimeout(resolve, 300));

                    // Login was started in the website drawer: go back there instead of continuing standalone.
                    if (!isSafeNext && embedReturnPage) {
                        window.location.replace(buildDrawerReopenUrl(embedReturnPage, funnelPath));
                        return;
                    }
                    navigate(isSafeNext ? next : funnelPath);
                } else {
                    setError(t('loginVerificationFailed') as string);
                    setIsVerifying(false);
                }
            } catch (err) {
                console.error("Login verification failed:", err);
                setError(t('loginVerificationFailed') as string);
                setIsVerifying(false);
            }
        };

        verifyToken();
    }, [token]);

    const handleTryAgain = () => {
        navigate("/login");
    };

    const handleBackToBooking = () => {
        const redirectTo = returnUrl || "/";
        clearReturnUrl();
        navigate(redirectTo);
    };

    return (
        <motion.div
            className="flex flex-col min-h-screen bg-light-burgundy"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
        >
            {/* Header - only show when there's an error */}
            {error && (
                <div className="flex items-center justify-between p-4">
                    <button
                        onClick={handleBackToBooking}
                        className="flex items-center gap-2 text-burgundy hover:text-burgundy/70 transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5" />
                        <span className="text-sm">{t('loginBackToBooking')}</span>
                    </button>
                </div>
            )}

            {/* Main Content */}
            <div className="flex-1 flex flex-col items-center justify-center px-6 pb-20">
                <motion.div
                    className="w-full max-w-md"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.1, ease: "easeOut" }}
                >
                    {/* Logo */}
                    <div className="flex justify-center mb-8">
                        <KaliaLogo />
                    </div>

                    {/* Card */}
                    <div className="bg-white rounded-lg p-6 shadow-md">
                        {isVerifying ? (
                            // Loading state
                            <div className="flex flex-col items-center py-8">
                                <Loader2 className="w-12 h-12 text-burgundy animate-spin mb-4" />
                                <p className="text-burgundy text-center">
                                    {t('loginVerifying')}
                                </p>
                            </div>
                        ) : error ? (
                            // Error state
                            <div className="flex flex-col items-center">
                                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
                                    <AlertCircle className="w-6 h-6 text-red-600" />
                                </div>
                                <p className="text-burgundy text-center mb-6">
                                    {error}
                                </p>
                                <PrimaryActionButton
                                    onClick={handleTryAgain}
                                    label={t('loginTryAgain') as string}
                                />
                            </div>
                        ) : null}
                    </div>
                </motion.div>
            </div>
        </motion.div>
    );
};

export default LoginCallback;
